import './OnboardingSetup.pcss';

import { ShareAltOutlined } from '@ant-design/icons';
import { Button, Typography, notification } from 'custom-test-antd';
import { FC, useState } from 'react';

import { OnboardingDto, Portal } from 'api/organization/onboardingTypes';

import { BranchRegistrationWorkflowAddForm } from './BranchRegistrationWorkflowAddForm';
import { BranchRegistrationWorkflowManageForm } from './BranchRegistrationWorkflowManageForm';
import { InviteCollaboratorsModal } from './InviteCollaboratorsModal';

interface OnboardingSetupProps {
  userIsOrganizationAdmin: boolean;
  flowId: string | null;
  onboarding: OnboardingDto;
  onCreateBranchRegistrationWorkflow: (values: { portal: Portal; login: string; password: string }) => Promise<void>;
  onUpdateBranchRegistrationWorkflowCredentials: (values: {
    id: string;
    portal: Portal;
    login: string;
    password: string;
  }) => Promise<void>;
  onExcludeBranchFromRegistrationWorkflow: (credentialId: string, provider: string) => Promise<void>;
  onIncludeBranchToRegistrationWorkflow: (credentialId: string, provider: string) => Promise<void>;
  onRemoveBranchRegistrationWorkflow: (id: string) => Promise<void>;
  onShareFlow: (collaboratorEmail?: string) => Promise<void>;
  onNext: () => void;
}

export const OnboardingSetup: FC<OnboardingSetupProps> = ({
  userIsOrganizationAdmin,
  flowId,
  onboarding,
  onCreateBranchRegistrationWorkflow,
  onUpdateBranchRegistrationWorkflowCredentials,
  onExcludeBranchFromRegistrationWorkflow,
  onIncludeBranchToRegistrationWorkflow,
  onRemoveBranchRegistrationWorkflow,
  onShareFlow,
  onNext,
}) => {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const handleNext = () => {
    if (onboarding.branchRegistrationWorkflows.length === 0) {
      notification.warning({
        message: 'Credentials Required',
        description: 'At least one credential is required to proceed.',
      });
      return;
    }

    // Check if there are any invalid credentials
    const hasInvalidCredentials = onboarding.branchRegistrationWorkflows.some(workflow => !workflow.areCredentialsValid);
    if (hasInvalidCredentials) {
      notification.warning({
        message: 'Invalid Credentials',
        description: 'Please fix all invalid credentials before proceeding.',
      });
      return;
    }

    onNext();
  };

  // Check if all existing credentials are valid
  const allCredentialsValid = onboarding.branchRegistrationWorkflows.length === 0 ||
    onboarding.branchRegistrationWorkflows.every(workflow => workflow.areCredentialsValid);

  return (
    <>
      <div className="onboarding-setup-wrapper">
        <div className="onboarding-setup-container">
          <Typography.Title level={2} className="page-title">
            Add Facility Credentials (UAE)
          </Typography.Title>
          <Typography.Paragraph className="page-description">
            Add credentials for the portals you use. All data is encrypted. A new form will appear automatically after
            validation.
          </Typography.Paragraph>
          {onboarding.branchRegistrationWorkflows.length > 0 && (
            <div className="credentials-list">
              {onboarding.branchRegistrationWorkflows.map((branchRegistrationWorkflow) => (
                <BranchRegistrationWorkflowManageForm
                  key={branchRegistrationWorkflow.id}
                  branchesTaken={onboarding.branchesBelongingToOtherOrganizations}
                  branchRegistrationWorkflow={branchRegistrationWorkflow}
                  branchRegistrationWorkflowCanBeManaged={userIsOrganizationAdmin || branchRegistrationWorkflow.isOwn}
                  onUpdateBranchRegistrationWorkflowCredentials={onUpdateBranchRegistrationWorkflowCredentials}
                  onExcludeBranchFromRegistrationWorkflow={onExcludeBranchFromRegistrationWorkflow}
                  onIncludeBranchToRegistrationWorkflow={onIncludeBranchToRegistrationWorkflow}
                  onRemoveBranchRegistrationWorkflow={onRemoveBranchRegistrationWorkflow}
                />
              ))}
            </div>
          )}
          {/* Only show add form when all existing credentials are valid */}
          {allCredentialsValid && (
            <BranchRegistrationWorkflowAddForm
              branchRegistrationWorkflowsCount={onboarding.branchRegistrationWorkflows.length}
              onCreateBranchRegistrationWorkflow={onCreateBranchRegistrationWorkflow}
            />
          )}
          <div className="footer-actions">
            <Button
              icon={<ShareAltOutlined />}
              className="share-button"
              disabled={flowId != null}
              onClick={async () => setIsInviteModalOpen(true)}
            >
              Share with your colleague
            </Button>
            <Button type="primary" onClick={handleNext} className="next-button">
              Next
            </Button>
          </div>
        </div>
      </div>
      <InviteCollaboratorsModal
        open={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onShareFlow={onShareFlow}
      />
    </>
  );
};

export default OnboardingSetup;
